import {
  ContractMapping,
  CustomFieldValues,
  FulfillmentTask,
  FulfillmentUpdate,
  GeneralUpdate,
  StripeConnectionUpdate,
  StripeSource,
  StripeSourceUpdate
} from "@/entities/stripeCheckout/types";

export const STRIPE_CHECKOUT_PREFIX = "integrations.stripeCheckoutSettings";

// The Stripe events the webhook takes. A delayed payment (a bank debit) only
// completes with the second one.
export const CHECKOUT_COMPLETED = "checkout.session.completed";
export const ASYNC_PAYMENT_SUCCEEDED =
  "checkout.session.async_payment_succeeded";

// The backend's limits on a save.
export const MAX_PLANS = 100;
export const MAX_CUSTOM_FIELDS = 100;
export const MAX_NAME_LENGTH = 255;
export const MAX_PLAN_LENGTH = 256;
export const MAX_SOURCE_LABEL_LENGTH = 100;
export const MAX_TITLE_TEMPLATE_LENGTH = 256;
export const MAX_CHECKLIST_ITEMS = 50;
export const MAX_CHECKLIST_ITEM_LENGTH = 500;
export const MAX_ADMINS = 50;
export const MAX_DUE_DAYS = 60;
export const MAX_BUFFER_DAYS = 366;

// The placeholders a contract name may use, in the order the backend lists them.
export const NAME_PLACEHOLDERS = [
  "company",
  "product",
  "plan",
  "source",
  "year"
] as const;

// What a Stripe purchase carries, offered when mapping a submitted field.
export const FIELD_MAP_SUGGESTIONS = [
  "email",
  "full_name",
  "external_id",
  "extra.stripe_session_id",
  "extra.stripe_customer_id",
  "extra.stripe_payment_intent_id",
  "extra.stripe_invoice_id",
  "extra.amount_subtotal",
  "extra.amount_tax",
  "extra.amount_total",
  "extra.stripe_fee",
  "extra.net_amount",
  "extra.customer_phone",
  "extra.customer_country",
  "extra.customer_address"
];

const ACCOUNT_PATTERN = /^acct_[A-Za-z0-9]+$/;
const SIGNING_SECRET_PATTERN = /^whsec_\S+$/;
const RESTRICTED_KEY_PATTERN = /^rk_(live|test)_\S+$/;
const DURATION_PATTERN = /^P(\d+)([YMWD])$/;
const CUSTOM_PREFIX = "custom:";

// One value per line; blank lines are ignored.
export const parseLines = (text: string): string[] =>
  text
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

const hasDuplicate = (values: string[]) =>
  new Set(values).size !== values.length;

export const sameList = (first: string[], second: string[]) =>
  first.length === second.length &&
  first.every((value, index) => value === second[index]);

// Connection

// The route payconnector takes Stripe's events on, one per account.
export const stripeWebhookUrl = (accountId: string) =>
  `${window.location.origin}/api/v1/payconnector/payments/webhooks/stripe/${accountId}`;

export type ConnectionError = "accountsRequired" | "invalidAccount";

export const connectionErrors = (
  update: StripeConnectionUpdate
): ConnectionError[] => {
  const errors: ConnectionError[] = [];
  if (update.connect && update.connectedAccounts.length === 0)
    errors.push("accountsRequired");
  if (update.connectedAccounts.some((id) => !ACCOUNT_PATTERN.test(id)))
    errors.push("invalidAccount");
  return errors;
};

// Completed checkouts are always taken; delayed payments are the only choice.
export const eventTypesFor = (delayedPayments: boolean): string[] =>
  delayedPayments
    ? [CHECKOUT_COMPLETED, ASYNC_PAYMENT_SUCCEEDED]
    : [CHECKOUT_COMPLETED];

export type SecretKind = "webhookSigningSecret" | "restrictedApiKey";

export const isValidSecret = (kind: SecretKind, value: string): boolean =>
  (kind === "webhookSigningSecret"
    ? SIGNING_SECRET_PATTERN
    : RESTRICTED_KEY_PATTERN
  ).test(value);

// Custom field values

export interface FieldRow {
  name: string;
  valueText: string;
  // The saved value and how it showed, so a value left alone keeps its type.
  saved?: { text: string; value: unknown };
}

const showValue = (value: unknown): string =>
  typeof value === "string" ? value : (JSON.stringify(value) ?? "");

export const fieldRows = (values: CustomFieldValues): FieldRow[] =>
  Object.entries(values).map(([name, value]) => {
    const text = showValue(value);
    return { name, valueText: text, saved: { text, value } };
  });

// A value typed in is saved as text; one left as it was keeps its saved value.
export const fieldValues = (rows: FieldRow[]): CustomFieldValues =>
  Object.fromEntries(
    rows.map((row) => [
      row.name.trim(),
      row.saved && row.saved.text === row.valueText
        ? row.saved.value
        : row.valueText
    ])
  );

export type FieldError = "fieldNameRequired" | "duplicateFieldName";

export const fieldErrors = (rows: FieldRow[]): FieldError[] => {
  const names = rows.map((row) => row.name.trim());
  const errors: FieldError[] = [];
  if (names.some((name) => !name)) errors.push("fieldNameRequired");
  if (hasDuplicate(names.filter(Boolean))) errors.push("duplicateFieldName");
  return errors;
};

// Durations

export const DURATION_UNITS = ["D", "W", "M", "Y"] as const;
export type DurationUnit = (typeof DURATION_UNITS)[number];

export const parseDuration = (
  duration: string | undefined
): { count: string; unit: DurationUnit } | null => {
  const match = DURATION_PATTERN.exec(duration ?? "");
  return match ? { count: match[1], unit: match[2] as DurationUnit } : null;
};

const isPositiveWhole = (text: string) => /^[1-9]\d*$/.test(text.trim());

// The mapping PUT needs every key, nested ones included: an omitted key is a
// 422 that writes nothing. The GET always carries every key; naming each one
// here keeps the body to exactly the keys the PUT knows.
export const fullMapping = (mapping: ContractMapping): ContractMapping => ({
  nameTemplate: mapping.nameTemplate,
  productId: mapping.productId,
  customFields: mapping.customFields,
  planCustomFields: mapping.planCustomFields,
  fieldMap: mapping.fieldMap,
  start: { rule: mapping.start.rule },
  end: {
    rule: mapping.end.rule,
    planField: mapping.end.planField,
    durations: mapping.end.durations,
    bufferDays: mapping.end.bufferDays
  },
  services: mapping.services.map(({ plan, serviceItemId }) => ({
    plan,
    serviceItemId
  }))
});

// Prices: one row per Stripe price, joining the services, durations and plan
// fields the mapping keeps apart.

export interface PriceRow {
  plan: string;
  serviceItemId: string | null;
  durationCount: string;
  durationUnit: DurationUnit;
  fields: FieldRow[];
}

export const priceRows = (mapping: ContractMapping): PriceRow[] => {
  const plans = [
    ...mapping.services.map((service) => service.plan),
    ...Object.keys(mapping.end.durations),
    ...Object.keys(mapping.planCustomFields)
  ];
  return [...new Set(plans)].map((plan) => {
    const duration = parseDuration(mapping.end.durations[plan]);
    return {
      plan,
      serviceItemId:
        mapping.services.find((service) => service.plan === plan)
          ?.serviceItemId ?? null,
      durationCount: duration?.count ?? "",
      durationUnit: duration?.unit ?? "M",
      fields: fieldRows(mapping.planCustomFields[plan] ?? {})
    };
  });
};

export type PriceError =
  | "planRequired"
  | "duplicatePlan"
  | "serviceItemRequired"
  | "durationRequired"
  | FieldError;

export const priceErrors = (rows: PriceRow[]): PriceError[] => {
  const plans = rows.map((row) => row.plan.trim());
  const errors: PriceError[] = [];
  if (plans.some((plan) => !plan)) errors.push("planRequired");
  if (hasDuplicate(plans.filter(Boolean))) errors.push("duplicatePlan");
  if (rows.some((row) => !row.serviceItemId))
    errors.push("serviceItemRequired");
  if (rows.some((row) => !isPositiveWhole(row.durationCount)))
    errors.push("durationRequired");
  const fieldProblems = new Set(rows.flatMap((row) => fieldErrors(row.fields)));
  return [...errors, ...fieldProblems];
};

export const mappingWithPrices = (
  mapping: ContractMapping,
  rows: PriceRow[]
): ContractMapping => {
  const priced = rows.map((row) => ({ ...row, plan: row.plan.trim() }));
  return fullMapping({
    ...mapping,
    services: priced.map((row) => ({
      plan: row.plan,
      serviceItemId: row.serviceItemId ?? ""
    })),
    end: {
      ...mapping.end,
      durations: Object.fromEntries(
        priced.map((row) => [
          row.plan,
          `P${Number(row.durationCount.trim())}${row.durationUnit}`
        ])
      )
    },
    planCustomFields: Object.fromEntries(
      priced
        .filter((row) => row.fields.length > 0)
        .map((row) => [row.plan, fieldValues(row.fields)])
    )
  });
};

// Field map: the target is a Contract custom field, saved as custom:<name>.

export interface FieldMapRow {
  source: string;
  target: string;
}

export const fieldMapRows = (fieldMap: Record<string, string>): FieldMapRow[] =>
  Object.entries(fieldMap).map(([source, target]) => ({
    source,
    target: target.startsWith(CUSTOM_PREFIX)
      ? target.slice(CUSTOM_PREFIX.length)
      : target
  }));

export const fieldMapFromRows = (rows: FieldMapRow[]): Record<string, string> =>
  Object.fromEntries(
    rows.map((row) => [
      row.source.trim(),
      `${CUSTOM_PREFIX}${row.target.trim()}`
    ])
  );

export type FieldMapError =
  | "sourceRequired"
  | "targetRequired"
  | "duplicateSource";

export const fieldMapErrors = (rows: FieldMapRow[]): FieldMapError[] => {
  const sources = rows.map((row) => row.source.trim());
  const errors: FieldMapError[] = [];
  if (sources.some((source) => !source)) errors.push("sourceRequired");
  if (rows.some((row) => !row.target.trim())) errors.push("targetRequired");
  if (hasDuplicate(sources.filter(Boolean))) errors.push("duplicateSource");
  return errors;
};

// Contract details

export interface ContractDetailsDraft {
  nameTemplate: string;
  productId: string | null;
  bufferDays: string;
  customFields: FieldRow[];
  fieldMap: FieldMapRow[];
}

export const contractDetailsDraft = (
  mapping: ContractMapping
): ContractDetailsDraft => ({
  nameTemplate: mapping.nameTemplate,
  productId: mapping.productId,
  bufferDays: String(mapping.end.bufferDays),
  customFields: fieldRows(mapping.customFields),
  fieldMap: fieldMapRows(mapping.fieldMap)
});

export type ContractDetailsError =
  | "nameTemplateRequired"
  | "unknownPlaceholder"
  | "bufferDaysInvalid"
  | FieldError
  | FieldMapError;

// The placeholders a template names; null when its braces don't pair up.
export const templatePlaceholders = (template: string): string[] | null => {
  const names: string[] = [];
  const pattern = /\{\{|\}\}|\{([^{}]*)\}|[{}]/g;
  for (const match of template.matchAll(pattern)) {
    if (match[0] === "{{" || match[0] === "}}") continue;
    if (match[1] === undefined) return null;
    names.push(match[1]);
  }
  return names;
};

export const contractDetailsErrors = (
  draft: ContractDetailsDraft
): ContractDetailsError[] => {
  const errors: ContractDetailsError[] = [];
  const template = draft.nameTemplate.trim();
  if (!template) errors.push("nameTemplateRequired");
  const placeholders = templatePlaceholders(template);
  if (
    template &&
    (placeholders === null ||
      placeholders.some(
        (name) => !(NAME_PLACEHOLDERS as readonly string[]).includes(name)
      ))
  )
    errors.push("unknownPlaceholder");
  const buffer = draft.bufferDays.trim();
  if (!/^\d+$/.test(buffer) || Number(buffer) > MAX_BUFFER_DAYS)
    errors.push("bufferDaysInvalid");
  return [
    ...errors,
    ...fieldErrors(draft.customFields),
    ...fieldMapErrors(draft.fieldMap)
  ];
};

export const mappingWithDetails = (
  mapping: ContractMapping,
  draft: ContractDetailsDraft
): ContractMapping =>
  fullMapping({
    ...mapping,
    nameTemplate: draft.nameTemplate.trim(),
    productId: draft.productId,
    customFields: fieldValues(draft.customFields),
    fieldMap: fieldMapFromRows(draft.fieldMap),
    end: { ...mapping.end, bufferDays: Number(draft.bufferDays.trim()) }
  });

// Company identification: the fields of the Stripe source other than its
// on/off switch, which saves on its own.

export interface SourceDraft {
  companyNameField: string;
  companyOverrideField: string;
  contractOverrideField: string;
  sourceLabel: string;
}

export const sourceDraft = (source: StripeSource): SourceDraft => ({
  companyNameField: source.companyNameField ?? "",
  companyOverrideField: source.companyOverrideField,
  contractOverrideField: source.contractOverrideField,
  sourceLabel: source.sourceLabel
});

export type SourceError =
  | "companyOverrideFieldRequired"
  | "contractOverrideFieldRequired";

export const sourceErrors = (draft: SourceDraft): SourceError[] => {
  const errors: SourceError[] = [];
  if (!draft.companyOverrideField.trim())
    errors.push("companyOverrideFieldRequired");
  if (!draft.contractOverrideField.trim())
    errors.push("contractOverrideFieldRequired");
  return errors;
};

export const sourceUpdate = (
  enabled: boolean,
  draft: SourceDraft
): StripeSourceUpdate => ({
  enabled,
  sourceLabel: draft.sourceLabel.trim(),
  companyNameField: draft.companyNameField.trim() || null,
  companyOverrideField: draft.companyOverrideField.trim(),
  contractOverrideField: draft.contractOverrideField.trim()
});

// Failure alerts

export const generalUpdate = (
  adminUserIds: string[],
  timezoneText: string
): GeneralUpdate => ({
  adminUserIds,
  timezone: timezoneText.trim() || null
});

export type GeneralError = "tooManyAdmins";

export const generalErrors = (update: GeneralUpdate): GeneralError[] =>
  update.adminUserIds.length > MAX_ADMINS ? ["tooManyAdmins"] : [];

// Fulfillment task

export const DEFAULT_TITLE_TEMPLATE = "Onboard {company}";
export const DEFAULT_DUE_DAYS = 2;

export interface FulfillmentDraft {
  enabled: boolean;
  assigneeId: string | null;
  titleTemplate: string;
  checklistText: string;
  dueInBusinessDays: string;
}

export const fulfillmentDraft = (
  task: FulfillmentTask | null
): FulfillmentDraft => ({
  enabled: task !== null,
  assigneeId: task?.assigneeId ?? null,
  titleTemplate: task?.titleTemplate ?? DEFAULT_TITLE_TEMPLATE,
  checklistText: (task?.checklist ?? []).join("\n"),
  dueInBusinessDays: String(task?.dueInBusinessDays ?? DEFAULT_DUE_DAYS)
});

export type FulfillmentError =
  | "assigneeRequired"
  | "titleRequired"
  | "tooManyChecklistItems"
  | "checklistItemTooLong"
  | "dueDaysInvalid";

export const fulfillmentErrors = (
  draft: FulfillmentDraft
): FulfillmentError[] => {
  if (!draft.enabled) return [];
  const errors: FulfillmentError[] = [];
  if (!draft.assigneeId) errors.push("assigneeRequired");
  if (!draft.titleTemplate.trim()) errors.push("titleRequired");
  const checklist = parseLines(draft.checklistText);
  if (checklist.length > MAX_CHECKLIST_ITEMS)
    errors.push("tooManyChecklistItems");
  if (checklist.some((item) => item.length > MAX_CHECKLIST_ITEM_LENGTH))
    errors.push("checklistItemTooLong");
  const due = draft.dueInBusinessDays.trim();
  if (!/^\d+$/.test(due) || Number(due) > MAX_DUE_DAYS)
    errors.push("dueDaysInvalid");
  return errors;
};

export const fulfillmentUpdate = (
  draft: FulfillmentDraft
): FulfillmentUpdate => ({
  fulfillmentTask:
    draft.enabled && draft.assigneeId
      ? {
          assigneeId: draft.assigneeId,
          titleTemplate: draft.titleTemplate.trim(),
          checklist: parseLines(draft.checklistText),
          dueInBusinessDays: Number(draft.dueInBusinessDays.trim())
        }
      : null
});
